import { getScheduleShiftCodeFontSize } from "@utils/fontSizeUtils";
import type * as type from "@vTypes";
import { cn } from "@utils/cn.ts";
import useStore from "@/store/store";
import { useState, useContext, type MouseEvent } from "react";
import ModifiedCellsContext from "@pages/roster-create/context/ModifiedCellsContext";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import EmergencyCode from "@/pages/roster-create/components/shift-code/emergency-code";
import ModifyCode from "@/pages/roster-create/components/shift-code/modify-code";
import EmergencySuccessCode from "@/pages/roster-create/components/shift-code/emergency-success-code";
import WantedMissmatchCode from "@/pages/roster-create/components/shift-code/wanted-missmatch-code";
import WantedNotApplied from "@/pages/roster-create/components/shift-code/wanted-not-applied";
import ViolationGroupBarCode from "@/pages/roster-create/components/shift-code/violation-group-bar-code";
import AssignmentGroupBar from "@/pages/roster-create/components/shift-code/assignment-group-bar";
import type { AssignmentStartInfo } from "@/services/assignment";
import EmergencyBulkRangeBar from "@/pages/roster-create/components/shift-code/emergency-bulk-range-bar";
import {
  ShiftCodeCell,
  type ShiftCodeCellMode,
} from "@/pages/roster-create/components/shift-code/shift-code-cell";
import { useRosterCreateUiStore } from "@/store/rosterCreateUiStore";
import { useRosterFocusStore } from "@/store/rosterFocusStore";

function selectedShiftDbId(
  sel: type.Type_availableShifts | null
): number | undefined {
  if (!sel || typeof sel !== "object") return undefined;
  const id = (sel as { id?: number }).id;
  if (id == null || !Number.isFinite(Number(id))) return undefined;
  return Number(id);
}

function resolveCellUiMode({
  isEmergencyTypeLocked,
  isWeeklyShiftLocked,
  /** 편집/긴급대체 모드가 활성일 때만 true로 전달 */
  isAssignmentLocked,
  shouldShowViolationMessages,
  hasComment,
  isWantedMismatch,
}: {
  isEmergencyTypeLocked: boolean;
  isWeeklyShiftLocked: boolean;
  isAssignmentLocked: boolean;
  shouldShowViolationMessages: boolean;
  hasComment: boolean;
  isWantedMismatch: boolean;
}): ShiftCodeCellMode {
  if (isEmergencyTypeLocked) return "emergency_locked";
  if (isWeeklyShiftLocked) return "weekly_locked";
  if (isAssignmentLocked) return "assignment_locked";
  if (shouldShowViolationMessages) return "violation_messages";
  if (hasComment) return "comment_popover";
  if (isWantedMismatch) return "wanted_mismatch";
  return "default";
}

function resolveEmergencyUiState({
  isEmergencyPickMode,
  emergencySlotPick,
  emergencyReplacementCandidateNurseId,
  emergencyPostApplyHighlight,
  emergencyPostApplySuccess,
  nurseId,
  day,
  displayCode,
}: {
  isEmergencyPickMode: boolean;
  emergencySlotPick: EmergencyReplacementSlotPick | null;
  emergencyReplacementCandidateNurseId: string | null;
  emergencyPostApplyHighlight: EmergencyPostApplyHighlight | null;
  emergencyPostApplySuccess: boolean | undefined;
  nurseId: string;
  day: number;
  displayCode: string;
}) {
  const dayIndex = day - 1;
  const currentNurseId = String(nurseId);
  const trimmedDisplayCode = String(displayCode).trim();
  const slotNurseId =
    emergencySlotPick != null ? String(emergencySlotPick.nurseId) : null;
  const isSameDayAsSlot =
    emergencySlotPick != null && emergencySlotPick.dayIndex === dayIndex;

  const isTargetSlotHighlighted =
    isEmergencyPickMode &&
    emergencySlotPick != null &&
    slotNurseId === currentNurseId &&
    isSameDayAsSlot &&
    trimmedDisplayCode !== "-" &&
    String(emergencySlotPick.shiftCode).trim() === trimmedDisplayCode;

  const isCandidateDayHighlighted =
    isEmergencyPickMode &&
    emergencyReplacementCandidateNurseId != null &&
    isSameDayAsSlot &&
    String(emergencyReplacementCandidateNurseId) === currentNurseId;

  const isPostApplyGlow =
    emergencyPostApplyHighlight != null &&
    emergencyPostApplyHighlight.dayIndex === dayIndex &&
    (String(emergencyPostApplyHighlight.targetNurseId) === currentNurseId ||
      String(emergencyPostApplyHighlight.replacementNurseId) ===
        currentNurseId);

  return {
    isEmergencyPickHighlighted:
      isTargetSlotHighlighted || isCandidateDayHighlighted,
    showEmergencyPostApplySuccess:
      emergencyPostApplySuccess !== undefined
        ? emergencyPostApplySuccess
        : isPostApplyGlow,
  };
}

function TdShiftCodeButton({
  isEveryFifth,
  nurse_id,
  nursesIndex,
  isFirstNurseRow = false,
  day,
  shift,
  modDayShift,
  selectedShift,
  isError,
  violationGroup, // 호환성을 위해 유지하지만 사용하지 않음 (violationGroups 사용)
  violationGroups,
  assignmentStart = null,
  violationMessages,
  comment,
  isApplied = true, // 기본값은 true (원티드 반영 설정에서만 사용)
  isWantedMismatch = false, // 원티드 데이터와 현재 근무코드 불일치
  wantedShiftId, // 원티드 데이터의 근무코드 (툴팁 표시용)
  /** `shift`가 없을 때(미배정 `-` 등)에도 원티드 미일치 표시용으로 근무표 원문 코드 */
  cellScheduleCode,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  /** 미지정 시 `emergencyPostApplyHighlight`로 추론. 상위에서 이 셀을 성공 데코할지 명시할 때 사용 */
  emergencyPostApplySuccess = undefined,
  nurseName = "",
  scheduleYear = 0,
  scheduleMonth = 0,
  /** 원티드 모달 등: 전역 스토어 대신 상위에서 넘긴 위반 표시 여부 사용 */
  violationVisibilityOverride = null,
  /** 원티드 조정판 등 uiMode가 "edit"이 아닌 컨텍스트에서 편집 가능 여부를 직접 지정 */
  isEditModeOverride,
  /** false: 셀 클릭으로 열·행 선택(클릭 하이라이트) 안 함 — 호버만 */
  selectionFocusOnClick = true,
  /** inbound 정책상 편집 불가 셀 (파견 나간 기간 or 타병동 간호사의 비파견 기간) */
  isAssignmentLocked = false,
}: {
  isEveryFifth?: boolean;
  nurse_id: string;
  nursesIndex: number;
  /** 첫 번째 간호사 행(`nursesIndex === 0`) — 상위에서만 전달, 기본 false */
  isFirstNurseRow?: boolean;
  day: number;
  shift:
    | {
        shift_id: string;
        name: string;
        color: string;
        type?: string;
        default_shift?: string | null;
        shift_gb?: string;
      }
    | undefined;
  modDayShift: (
    nurseIndex: number,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  selectedShift: type.Type_availableShifts | null;
  isError: boolean;
  violationGroup?: {
    type: string;
    pattern?: string;
    startDay: number;
    endDay: number;
    rangeLength?: number;
    isStart: boolean;
    isEnd: boolean;
  } | null;
  violationGroups?: Array<{
    type: string;
    pattern?: string;
    startDay: number;
    endDay: number;
    rangeLength?: number;
    isStart: boolean;
    isEnd: boolean;
  }> | null;
  /** 기간배정 오버레이 — 시작 셀에서만 전달, 나머지 셀은 null */
  assignmentStart?: AssignmentStartInfo | null;
  violationMessages?: string[];
  comment?: string; // 원티드 신청 메시지 (comment)
  isApplied?: boolean; // 원티드 반영 설정용: is_applied 정보 (false일 때 X 아이콘 표시)
  isWantedMismatch?: boolean; // 원티드 데이터와 현재 근무코드 불일치
  wantedShiftId?: string; // 원티드 데이터의 근무코드 (툴팁 표시용)
  cellScheduleCode?: string | null;
  /** 긴급대체 모달 열림 시 셀 클릭으로 슬롯 선택 (당일 모드) */
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  /** 긴급대체 모달에서 현재 선택된 슬롯(테이블 인디케이터) */
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  /** 기간 지정 모드: 셀 클릭 콜백 (상태 머신 로직은 훅에서 처리) */
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  /** 기간 지정 모드: 현재 선택된 범위 (하이라이트 바 렌더용) */
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  /** BULK 시나리오 선택된 steps: 해당 날짜+대체 간호사 셀 하이라이트 */
  emergencyBulkSelectedSteps?: Array<{
    date: string;
    nurseId: string;
  }> | null;
  /** 모달 추천 리스트에서 고른 대체 간호사 — 대상일 셀 하이라이트 */
  emergencyReplacementCandidateNurseId?: string | null;
  /** 설정완료 직후 ~10초: 원래 대상·대체자 셀 좌표 하이라이트(모달 off·코드 변경과 무관) */
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  /** 성공 데코 표시 여부. `undefined`면 highlight 좌표로 자동 판별 */
  emergencyPostApplySuccess?: boolean;
  nurseName?: string;
  scheduleYear?: number;
  scheduleMonth?: number;
  violationVisibilityOverride?: {
    showConfigViolations: boolean;
    showManpowerViolations: boolean;
  } | null;
  isEditModeOverride?: boolean;
  selectionFocusOnClick?: boolean;
  isAssignmentLocked?: boolean;
}) {
  const rosterCreateUiMode = useRosterCreateUiStore((state) => state.mode);
  const isEditActive = isEditModeOverride ?? rosterCreateUiMode === "edit";

  void isEveryFifth;
  /** 스케줄에 저장된 원문(예: 옛날 값이면 여전히 D) */
  const displayCode =
    cellScheduleCode === null ||
    cellScheduleCode === undefined ||
    String(cellScheduleCode).trim() === ""
      ? "-"
      : String(cellScheduleCode);
  /**
   * 셀 표시·data용 근무코드: 설정에서 D→DD로 바뀌어도 resolve된 메타의 shift_id(DD)를 사용.
   * 메타 없으면 스케줄 원문 그대로.
   */
  const displayShiftId =
    shift?.shift_id ?? (displayCode === "-" ? "-" : displayCode);
  const shiftText =
    selectedShift && selectedShift.shift_id ? selectedShift.shift_id : "";

  const {
    showManpowerViolations: storeManpower,
    showConfigViolations: storeConfig,
  } = useStore();
  const showManpowerViolations =
    violationVisibilityOverride?.showManpowerViolations ?? storeManpower;
  const showConfigViolations =
    violationVisibilityOverride?.showConfigViolations ?? storeConfig;
  const isWantedBoardReadonly =
    violationVisibilityOverride != null && !isEditActive;

  // Popover hover 상태 관리
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // 수정된 셀인지 확인 (Context로 직접 접근 - prop drilling 불필요)
  const { modifiedCells } = useContext(ModifiedCellsContext);
  const isModified = modifiedCells.has(`${nursesIndex}-${day - 1}`);

  const isManpowerMessage = (msg: string) =>
    msg.includes("인원 미달") || msg.includes("인원 초과");
  const isManpowerType = (type: string) =>
    type === "manpower_shortage" || type === "manpower_excess";
  const manpowerViolationMessages =
    violationMessages?.filter((msg) => isManpowerMessage(msg)) || [];
  const configViolationMessages =
    violationMessages?.filter((msg) => !isManpowerMessage(msg)) || [];

  // 표시할 위반사항 결정
  // 여러 위반이 있을 수 있으므로 violationGroups 배열 사용
  // violationGroup은 호환성을 위해 유지하지만 사용하지 않음
  void violationGroup;
  const visibleViolationGroups =
    violationGroups?.filter((vg) => {
      return (
        (isManpowerType(vg.type) && showManpowerViolations) ||
        (!isManpowerType(vg.type) && showConfigViolations)
      );
    }) || [];

  // 위반 메시지는 최소인원/설정을 합쳐서 모두 노출
  // - 최소인원 메시지는 실제 에러 셀(isError)일 때만 노출
  const displayedViolationMessages = [
    ...(showManpowerViolations && isError ? manpowerViolationMessages : []),
    ...(showConfigViolations ? configViolationMessages : []),
  ];
  const shouldShowViolationMessages =
    !isWantedBoardReadonly && displayedViolationMessages.length > 0;

  // BULK 시나리오 step 하이라이트: 선택된 시나리오에서 이 셀(간호사+날짜)이 대체 투입 대상인지
  const isBulkStepHighlighted = (() => {
    if (!emergencyBulkSelectedSteps || !scheduleYear || !scheduleMonth)
      return false;
    const dateISO = `${scheduleYear}-${String(scheduleMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return emergencyBulkSelectedSteps.some(
      (s) => s.date === dateISO && String(s.nurseId) === String(nurse_id)
    );
  })();

  // 기간 지정 범위 바: 이 셀이 시작 셀인지 판별
  const dayIndex = day - 1;
  const bulkRangeBar = (() => {
    if (
      (!isEmergencyBulkPickMode && !isEmergencyPickMode) ||
      !emergencyBulkPeriodPick?.endDateISO
    ) {
      return null;
    }
    if (String(emergencyBulkPeriodPick.nurseId) !== String(nurse_id))
      return null;
    if (emergencyBulkPeriodPick.startDayIndex !== dayIndex) return null;
    const endIdx = emergencyBulkPeriodPick.endDayIndex;
    const isComplete = endIdx != null;
    const rangeLength = isComplete
      ? endIdx - emergencyBulkPeriodPick.startDayIndex + 1
      : 1;
    return { rangeLength, isComplete };
  })();

  /** 긴급대체 모달(당일): 수정 모드보다 우선해 셀만 선택 */
  const runEmergencyPickIfActive = (e: MouseEvent): boolean => {
    if (!isEmergencyPickMode || !onEmergencySlotPick) return false;
    if (!displayCode || displayCode === "-") {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    const y = scheduleYear;
    const m = scheduleMonth;
    if (!y || !m) {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    const dateISO = `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    onEmergencySlotPick({
      nurseId: nurse_id,
      nurseName,
      dayIndex: day - 1,
      shiftCode: displayCode.trim(),
      dateISO,
      shiftColor: shift?.color,
      shiftLabel: displayShiftId,
    });
    e.preventDefault();
    e.stopPropagation();
    return true;
  };

  /** 긴급대체 기간 지정: 범위 선택 클릭 (상태 머신은 훅에서 처리) */
  const runEmergencyBulkPickIfActive = (e: MouseEvent): boolean => {
    if (!isEmergencyBulkPickMode || !onEmergencyBulkPick) return false;
    if (!displayCode || displayCode === "-") {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    const y = scheduleYear;
    const m = scheduleMonth;
    if (!y || !m) {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    const dateISO = `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    onEmergencyBulkPick({
      nurseId: nurse_id,
      nurseName,
      dayIndex: day - 1,
      dateISO,
      shiftColor: shift?.color,
    });
    e.preventDefault();
    e.stopPropagation();
    return true;
  };

  const { isEmergencyPickHighlighted, showEmergencyPostApplySuccess } =
    resolveEmergencyUiState({
      isEmergencyPickMode,
      emergencySlotPick,
      emergencyReplacementCandidateNurseId,
      emergencyPostApplyHighlight,
      emergencyPostApplySuccess,
      nurseId: nurse_id,
      day,
      displayCode,
    });

  const isEmergencyTypeLocked =
    isEmergencyPickMode && !!shift && shift.type !== "근무";
  // 주휴: 근무코드 추가(선택) 칠하기 모드일 때만 변경 불가 툴팁
  const isWeeklyShiftLocked =
    isEditActive && shift?.shift_id === "주" && selectedShift != null;

  // 편집 모드 또는 긴급대체 모드가 활성일 때만 잠금 톨팁 표시
  const isInteractiveMode =
    isEditActive || isEmergencyPickMode || isEmergencyBulkPickMode;

  const cellUiMode = resolveCellUiMode({
    isEmergencyTypeLocked,
    isWeeklyShiftLocked,
    isAssignmentLocked: isAssignmentLocked && isInteractiveMode,
    shouldShowViolationMessages,
    hasComment: Boolean(comment),
    isWantedMismatch: isWantedBoardReadonly ? false : isWantedMismatch,
  });

  const fontSizeValue = getScheduleShiftCodeFontSize(displayShiftId);
  const setHoveredFocus = useRosterFocusStore((state) => state.setHoveredFocus);
  const clearHoveredFocus = useRosterFocusStore(
    (state) => state.clearHoveredFocus
  );
  const setSelectedFocus = useRosterFocusStore(
    (state) => state.setSelectedFocus
  );

  const dayColumnKey = `day${day}`;

  const handleWeeklyLockedClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (runEmergencyBulkPickIfActive(e)) return;
    if (runEmergencyPickIfActive(e)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEmergencyLockedClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleShiftCellClickEditOrFocus = (
    e: MouseEvent<HTMLButtonElement>
  ) => {
    if (runEmergencyBulkPickIfActive(e)) return;
    if (runEmergencyPickIfActive(e)) return;

    const col = `day${day}`;
    const nid = String(nurse_id);

    if (selectionFocusOnClick) {
      const focus = useRosterFocusStore.getState();
      if (focus.selectedColumn === col && focus.selectedNurseId === nid) {
        focus.clearSelectedFocus();
        return;
      }
    }

    if (isEditActive) {
      if (!isAssignmentLocked) {
        modDayShift(
          nursesIndex,
          day - 1,
          shiftText,
          selectedShiftDbId(selectedShift)
        );
      }
    } else {
      e.stopPropagation();
    }

    if (selectionFocusOnClick) {
      setSelectedFocus({
        column: col,
        nurseId: nid,
      });
    }
  };

  const dataDefaultShift = shift?.default_shift || "";
  const dataShiftGb = shift?.shift_gb || "";
  const dataShiftId =
    cellUiMode === "wanted_mismatch" || cellUiMode === "default"
      ? shift?.shift_id || displayShiftId || ""
      : shift?.shift_id || "";
  const shiftBgColor = shift?.color ?? "#F0F0F2";

  return (
    <div
      data-column={dayColumnKey}
      {...(isFirstNurseRow ? { "data-roster-first-nurse-row": "" } : {})}
      className={cn(
        (isEmergencyPickMode || isEmergencyBulkPickMode) && "cursor-crosshair"
      )}
    >
      <div
        className="p-[3px] relative flex items-center"
        data-roster-shift-cell=""
      >
        {assignmentStart && (
          <AssignmentGroupBar
            key={`${nursesIndex}-${day}-asgn-${assignmentStart.reason}-${assignmentStart.startDay}`}
            group={assignmentStart}
          />
        )}

        {/* 위반 그룹 시각적 묶음 표시 (여러 위반 중첩 가능) */}
        {visibleViolationGroups.length > 0 &&
          visibleViolationGroups.map((vg) => {
            if (!vg.isStart) return null;
            const stableKey = `${nursesIndex}-${day}-${vg.type}-${vg.startDay}-${vg.endDay}`;
            return <ViolationGroupBarCode key={stableKey} group={vg} />;
          })}

        {/* 기간 지정 범위 선택 바 (시작 셀에서만) */}
        {bulkRangeBar && (
          <EmergencyBulkRangeBar
            rangeLength={bulkRangeBar.rangeLength}
            isComplete={bulkRangeBar.isComplete}
          />
        )}

        {/* BULK 시나리오 선택: 대체 투입 셀 — 당일 긴급과 동일(빨강 + 벨) */}
        {isBulkStepHighlighted && <EmergencyCode />}

        {/* 주휴 */}
        {/* {shift?.shift_id === "주" && <OffIconWrapper />} */}

        {/* 원티드 반영 설정: is_applied=false일 때 X 아이콘 표시 (원티드 반영 설정 모달에서만) */}
        {/* isApplied가 undefined가 아니고 false일 때만 표시 (일반 화면에서는 isAppliedMap이 없음) */}
        {isApplied !== undefined && !isApplied && shift && <WantedNotApplied />}

        {/* 원티드 데이터와 현재 근무코드 불일치 표시 (시각적 표시만) — 미배정(-)도 shift 메타 없이 표시 */}
        {isWantedMismatch && <WantedMissmatchCode />}

        {/* 긴급대체: 모달에서 슬롯 선택 중 — 빨강 + 긴급 아이콘 */}
        {isEmergencyPickHighlighted && <EmergencyCode />}

        {/* 수정된 셀 배지 (하얀 배경, 파란 테두리와 펜) */}
        {isModified && <ModifyCode />}

        {/* 긴급대체: 설정완료 직후 성공 — 바깥은 투명(셀 색 유지), 아이콘 배지만 bg-white + 체크 sig */}
        {showEmergencyPostApplySuccess && <EmergencySuccessCode />}

        <div className={cn("d-f jc-c ai-c h-100p ")}>
          <ShiftCodeCell
            cellUiMode={cellUiMode}
            displayShiftId={displayShiftId}
            fontSize={fontSizeValue}
            nurseId={nurse_id}
            day={day}
            dataShiftId={dataShiftId}
            dataDefaultShift={dataDefaultShift}
            dataShiftGb={dataShiftGb}
            dataIsError={cellUiMode === "default" ? undefined : isError}
            shiftBgColor={shiftBgColor}
            onWeeklyLockedClick={handleWeeklyLockedClick}
            onEmergencyLockedClick={handleEmergencyLockedClick}
            onShiftCellClickEditOrFocus={handleShiftCellClickEditOrFocus}
            onMouseEnterCell={() => {
              setHoveredFocus({
                column: `day${day}`,
                nurseId: String(nurse_id),
              });
            }}
            onMouseLeaveCell={() => {
              clearHoveredFocus();
            }}
            onMouseEnterCommentPopover={() => {
              setHoveredFocus({
                column: `day${day}`,
                nurseId: String(nurse_id),
              });
              setIsPopoverOpen(true);
            }}
            onMouseLeaveCommentPopover={() => {
              clearHoveredFocus();
              setIsPopoverOpen(false);
            }}
            displayedViolationMessages={displayedViolationMessages}
            comment={comment}
            isPopoverOpen={isPopoverOpen}
            onPopoverOpenChange={setIsPopoverOpen}
            onPopoverContentMouseEnter={() => setIsPopoverOpen(true)}
            onPopoverContentMouseLeave={() => setIsPopoverOpen(false)}
            wantedShiftId={wantedShiftId}
          />

          <div className="errorShift">
            <div className="errorBadge"></div>
            <ul className="errorMessage"></ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/** `hoveredColumn` 등 스토어 구독이 있어 memo 제거 — props 동일 시에도 열 호버 리렌더 필요 */
export default TdShiftCodeButton;
